import { useMemo } from 'react';
import { Stack, TextField, Typography, Autocomplete, Chip, alpha, useTheme } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';

interface Resource {
    id: number;
    name: string;
}

interface MetricsUserFilterProps {
    resources: Resource[];
    selectedId: number | null;
    onChange: (id: number | null) => void;
}

export const MetricsUserFilter = ({ resources, selectedId, onChange }: MetricsUserFilterProps) => {
    const { LL } = useI18nContext();
    const theme = useTheme();

    const selected = useMemo(
        () => resources.find((r) => r.id === selectedId) ?? null,
        [resources, selectedId]
    );

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            sx={{
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 2,
                mb: 3,
                p: 2,
                borderRadius: 2,
                background: alpha(theme.palette.secondary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
            }}
        >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <PersonIcon fontSize="small" color="secondary" />
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                    }}
                >
                    {LL.metricsPage.userFilter.label()}
                </Typography>
            </Stack>
            <Autocomplete
                options={resources}
                value={selected}
                getOptionLabel={(o) => o.name}
                onChange={(_, v) => onChange(v?.id ?? null)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        placeholder={LL.metricsPage.userFilter.placeholder()}
                        sx={{ minWidth: 220 }}
                    />
                )}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                            <PersonIcon fontSize="small" color="action" />
                            {option.name}
                        </Stack>
                    </li>
                )}
                sx={{ flex: 1, maxWidth: 360 }}
                clearOnEscape
            />
            {selectedId !== null && (
                <Chip
                    label={LL.metricsPage.userFilter.allUsers()}
                    size="small"
                    onDelete={() => onChange(null)}
                    color="secondary"
                    variant="outlined"
                />
            )}
        </Stack>
    );
};
